import { AsyncPipe, DecimalPipe } from "@angular/common";
import { Component, DestroyRef, inject, input, type OnDestroy, type OnInit, output } from "@angular/core";
import { Router } from "@angular/router";
import { AlertService, Tag } from "@ui";
import Keycloak from "keycloak-js";
import {
  filter,
  finalize,
  firstValueFrom,
  map,
  type Observable,
  type Subscription,
  skipWhile,
  switchMap,
  timer,
} from "rxjs";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import type { CreateModelDataType, DTOSourceFilePartial, Pif } from "@pienso/ingest-types/pif.interface";
import { type StatusResponse, TaskType } from "@pienso/ingest-types/task.interface";
import { ConfigService } from "../../services/config.service";
import { CorpusDataService } from "../../services/corpus-data.service";
import { ElasticSearchService } from "../../services/elastic-search.service";
import { PifDataService } from "../../services/pif-data.service";
import { TaskDataService } from "../../services/task-data.service";
import { IngestActionCardComponent } from "./action-card.component";

// Action view for the ingest modal: replaces the "Next Steps" finish-modal from the old TrainingDataComponent.
@Component({
  selector: "app-ingest-modal-action",
  templateUrl: "./action.component.html",
  styleUrls: ["./action.component.scss"],
  imports: [AsyncPipe, DecimalPipe, Tag, IngestActionCardComponent],
})
export class IngestModalActionComponent implements OnInit, OnDestroy {
  private readonly keycloak = inject(Keycloak);
  private corpusService = inject(CorpusDataService);
  private elastic = inject(ElasticSearchService);
  private pifService = inject(PifDataService);
  private configService = inject(ConfigService);
  private taskService = inject(TaskDataService);
  private router = inject(Router);
  private alertService = inject(AlertService).withDestroyRef(inject(DestroyRef));

  readonly corpusId = input.required<number>();
  readonly pifId = input.required<number>();

  // emitted when this view is done and the modal should close
  readonly closed = output<void>();

  corpus: Corpus | null = null;
  pif: Pif | null = null;

  isDownloading = false;
  isSending = false; // non-specific sending operation
  isFingerprinting = false;
  isDeepLearning = false;
  isAnnotating = false;
  isUnitAnalyzing = false;
  isDashboarding = false;
  isSendingToExplore = false;

  piensoApps: string[] = [];
  minFPRows = 1000; // fingerprint
  maxFPRows = 150000; // fingerprint
  minDLRows = 5000; // deep learning
  minUnitAnalysisRows = 1;
  maxUnitAnalysisRows = 150000;
  rowCount = 0;
  hasDate = false;
  coreWebUrl: string | null = null;
  dashboardWebUrl: string | null = null;
  exploreWebUrl: string | null = null;

  isDeepLearningEnabled$!: Observable<boolean>;

  private taskSubscription: Subscription | null = null;

  ngOnInit() {
    this.isDeepLearningEnabled$ = this.configService
      .get("hardware.pool_training_gpus")
      .pipe(map((response) => parseInt(response[0]) > 0));

    firstValueFrom(
      this.configService.get("pienso_apps", "fp_row_min", "fp_row_max", "unit_row_min", "unit_row_max"),
    ).then((values) => {
      this.piensoApps = values[0].replace(/"/g, "").split(" ");
      const nums = values.slice(1).map((v) => parseInt(v, 10));
      this.minFPRows = nums[0];
      this.maxFPRows = nums[1];
      this.minUnitAnalysisRows = nums[2];
      this.maxUnitAnalysisRows = nums[3];
    });

    this.corpusService
      .getById(this.corpusId())
      .pipe(
        switchMap((corpus) => {
          this.corpus = corpus;
          return this.elastic.getColumnProperties(corpus.esIndex).pipe(map((columnProps) => ({ corpus, columnProps })));
        }),
        switchMap(({ corpus, columnProps }) => {
          this.hasDate = Object.values(columnProps).some((v) => v.type === "date");
          return this.elastic.countAll(corpus.esIndex);
        }),
        switchMap((count) => {
          this.rowCount = count;
          return this.configService.get("core_web_url", "dashboard_web_url", "explore_web_url");
        }),
        switchMap((urls: string[]) => {
          let [coreWebUrl, dashboardWebUrl, exploreWebUrl] = urls;
          coreWebUrl = coreWebUrl?.trim();
          dashboardWebUrl = dashboardWebUrl?.trim();
          exploreWebUrl = exploreWebUrl?.trim();
          this.coreWebUrl = coreWebUrl?.length > 0 ? coreWebUrl : null;
          this.dashboardWebUrl = dashboardWebUrl?.length > 0 ? dashboardWebUrl : null;
          this.exploreWebUrl = exploreWebUrl?.length > 0 ? exploreWebUrl : null;
          if (!this.coreWebUrl) {
            this.alertService.warn(
              "A connection to Pienso Core has not been configured. Model training, unit analysis and classification are not available.",
            );
          }
          return this.pifService.getById(this.pifId());
        }),
      )
      .subscribe({
        next: (pif: Pif) => (this.pif = pif),
        error: (e) => this.showError(e),
      });

    // track state of pif download and send-to-core jobs
    this.taskSubscription = timer(0, 5000)
      .pipe(
        skipWhile(() => !this.pifId()),
        switchMap(() => this.taskService.getOpenTasks([TaskType.PIF_DOWNLOAD, TaskType.SEND_PIF_TO_CORE])),
        map((responses: StatusResponse[]) => responses.filter((sr) => sr.entityId === this.pifId())),
        filter((responses: StatusResponse[]) => !!responses.length),
      )
      .subscribe((responses: StatusResponse[]) => {
        this.isDownloading = responses.filter((sr) => sr.type === TaskType.PIF_DOWNLOAD).length > 0;
        this.isSending = responses.filter((sr) => sr.type === TaskType.SEND_PIF_TO_CORE).length > 0;
      });
  }

  ngOnDestroy() {
    this.taskSubscription?.unsubscribe();
  }

  hasRoleAccess(role: string): boolean {
    return this.keycloak.tokenParsed?.realm_access?.roles?.includes(role) ?? false;
  }

  pifName() {
    return this.pif?.pifName ?? "";
  }

  coreExists() {
    return this.piensoApps.includes("CORE");
  }
  analysisExists() {
    return this.piensoApps.includes("ANALYSIS");
  }
  exploreExists() {
    return this.piensoApps.includes("EXPLORE");
  }

  canDownload(): boolean {
    return this.isValid() && !this.isActionInProgress();
  }

  canFingerprint(): boolean {
    return (
      this.coreExists() &&
      this.isValid() &&
      !this.isActionInProgress() &&
      !!this.coreWebUrl &&
      this.rowCount >= this.minFPRows &&
      this.rowCount <= this.maxFPRows &&
      this.hasRoleAccess("train:fingerprinting:full")
    );
  }

  canDL(): boolean {
    return (
      this.coreExists() &&
      this.isValid() &&
      !this.isActionInProgress() &&
      !!this.coreWebUrl &&
      this.pif?.mapping?.tagsCol != null &&
      this.rowCount >= this.minDLRows &&
      this.hasRoleAccess("train:deep_learning:full")
    );
  }

  canAnnotate(): boolean {
    return (
      this.coreExists() &&
      this.isValid() &&
      !this.isActionInProgress() &&
      !!this.coreWebUrl &&
      this.rowCount >= this.minDLRows &&
      this.hasRoleAccess("train:training_data:full")
    );
  }

  canUnitAnalyze(): boolean {
    return (
      this.coreExists() &&
      this.isValid() &&
      !this.isActionInProgress() &&
      !!this.coreWebUrl &&
      this.rowCount <= this.maxUnitAnalysisRows &&
      this.hasRoleAccess("analyze:data_set_analysis:full")
    );
  }

  canDashboard(): boolean {
    return (
      this.analysisExists() &&
      this.isValid() &&
      !this.isActionInProgress() &&
      !!this.dashboardWebUrl &&
      this.hasDate &&
      !!this.pif?.mapping?.dateCol &&
      this.hasRoleAccess("analyze:dashboards:full")
    );
  }

  canExplore(): boolean {
    return (
      this.exploreExists() &&
      this.isValid() &&
      !this.isActionInProgress() &&
      !!this.exploreWebUrl &&
      this.hasRoleAccess("analyze:explore:full")
    );
  }

  fingerprintReason(): string {
    if (!this.hasRoleAccess("train:fingerprinting:full")) {
      return "Access permissions required.";
    }
    if (this.rowCount < this.minFPRows) {
      return `Not enough rows. Model training requires at least ${this.minFPRows} rows.`;
    }
    if (this.rowCount > this.maxFPRows) {
      return `Too many rows. Model training requires a maximum of ${this.maxFPRows} rows.`;
    }
    return "";
  }

  deepLearningReason(): string {
    if (!this.hasRoleAccess("train:deep_learning:full")) {
      return "Access permissions required.";
    }
    if (this.pif?.mapping?.tagsCol == null) {
      return "Requires a 'labels' column.";
    }
    if (this.rowCount < this.minDLRows) {
      return `Not enough rows. Model training requires at least ${this.minDLRows} rows.`;
    }
    return "";
  }

  annotateReason(): string {
    if (!this.hasRoleAccess("train:training_data:full")) {
      return "Access permissions required.";
    }
    if (this.rowCount < this.minDLRows) {
      return `Not enough rows. Model training requires at least ${this.minDLRows} rows.`;
    }
    return "";
  }

  unitAnalysisReason(): string {
    if (!this.hasRoleAccess("analyze:data_set_analysis:full")) {
      return "Access permissions required.";
    }
    if (this.rowCount > this.maxUnitAnalysisRows) {
      return `Too many rows. Data set analysis requires a maximum of ${this.maxUnitAnalysisRows} rows.`;
    }
    return "";
  }

  dashboardReason(): string {
    if (!this.hasRoleAccess("analyze:dashboards:full")) {
      return "Access permissions required.";
    }
    if (!this.hasDate || !this.pif?.mapping?.dateCol) {
      return "Publishing to a dashboard requires a date column.";
    }
    return "";
  }

  exploreReason(): string {
    return this.hasRoleAccess("analyze:explore:full") ? "" : "Access permissions required.";
  }

  isValid(): boolean {
    return !!this.pif && !!this.pif.contentCol;
  }

  isActionInProgress() {
    return (
      this.isDownloading ||
      this.isSending ||
      this.isFingerprinting ||
      this.isDeepLearning ||
      this.isAnnotating ||
      this.isUnitAnalyzing
    );
  }

  download() {
    if (!this.pif) {
      return;
    }
    this.isDownloading = true;
    this.pifService
      .download(this.pif)
      .pipe(finalize(() => (this.isDownloading = false)))
      .subscribe({
        error: (e) => this.showError(e),
      });
  }

  trainFPModel() {
    this.sendToCore("fp", (loading) => (this.isFingerprinting = loading));
  }

  trainDLModel() {
    this.sendToCore("dl", (loading) => (this.isDeepLearning = loading));
  }

  annotateData() {
    this.sendToCore("annotate", (loading) => (this.isAnnotating = loading));
  }

  unitAnalyze() {
    this.sendToCore("unit", (loading) => (this.isUnitAnalyzing = loading));
  }

  dashboard() {
    this.sendToDashboard();
  }

  sendToExplore() {
    this._sendToExplore();
  }

  sendToCore(type: CreateModelDataType, setLoading: (loading: boolean) => void) {
    if (!this.pif) {
      this.showError(new Error("PIF is not set"));
      return;
    }
    setLoading(true);
    this.isSending = true;
    this.pifService
      .sendPifToCore(this.pif, type)
      .pipe(
        finalize(() => {
          setLoading(false);
          this.isSending = false;
        }),
      )
      .subscribe({
        next: (r: DTOSourceFilePartial) => {
          this.closed.emit();
          let route: string;
          switch (type) {
            case "dl":
              route = "dl";
              break;
            case "annotate":
              route = "trainingData";
              break;
            case "unit":
              route = "units";
              break;
            case "fp":
            default:
              route = "lenses";
              break;
          }
          const url = `/core/${route}?new=true&source=${r.id}&from=ingest&name=${encodeURIComponent(this.pifName())}`;
          this.router.navigateByUrl(url);
        },
        error: (e) => this.showError(e),
      });
  }

  sendToDashboard() {
    if (!this.pif) {
      this.showError(new Error("PIF is not set"));
      return;
    }
    this.isDashboarding = true;
    this.isSending = true;
    this.pifService
      .sendPifToDashboard(this.pif)
      .pipe(
        finalize(() => {
          this.isDashboarding = false;
          this.isSending = false;
        }),
      )
      .subscribe({
        next: (r: { fileKey: string }) => {
          this.closed.emit();
          const url = `/analysis/deploy?new=true&name=${encodeURIComponent(this.pifName())}&key=${encodeURIComponent(r.fileKey)}`;
          this.router.navigateByUrl(url);
        },
        error: (e) => this.showError(e),
      });
  }

  private _sendToExplore() {
    if (!this.pif) {
      this.showError(new Error("PIF is not set"));
      return;
    }
    this.isSendingToExplore = true;
    this.isSending = true;
    this.pifService
      .sendPifToExplore(this.pif)
      .pipe(
        finalize(() => {
          this.isSendingToExplore = false;
          this.isSending = false;
        }),
      )
      .subscribe({
        next: () => {
          this.closed.emit();
          this.router.navigateByUrl("/explore/browse/data");
        },
        error: (e) => this.showError(e),
      });
  }

  // http errors carry the useful text on error.error.message rather than error.message
  private showError(e: Error) {
    this.alertService.error((e as { error?: { message?: string } }).error?.message ?? e.message);
  }
}
